/**
 * Web attribution pass-through for OAuth connect flows.
 *
 * Capture: dual-write URL params to sessionStorage + localStorage.
 * Append: waterfall URL → sessionStorage → localStorage → `_fbc` (fbclid only),
 * plus context fields (referrer, anon_id, browser, client_device,
 * homepage_version / header_image_version / header_headline_version when the
 * user actually saw that homepage A/B arm).
 */
(function () {
  var KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
    'utm_interest',
    'utm_funnel',
    'utm_generic',
    'fbclid'
  ];

  /** Context keys appended to auth URLs (not always from the landing query string). */
  var CONTEXT_KEYS = [
    'referrer_url',
    'anon_id',
    'browser',
    'client_device',
    'homepage_version',
    'header_image_version',
    'header_headline_version'
  ];

  var REFERRER_MAX_LEN = 500;

  function getCookie(name) {
    try {
      var match = document.cookie.match(
        new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
      );
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function normalizeValue(key, value) {
    if (value == null) return '';
    // fbclid must be stored exactly as received — Meta docs: case sensitive, no modifications.
    if (key === 'fbclid') return String(value);
    return String(value).trim();
  }

  function isNonEmpty(key, value) {
    if (value == null) return false;
    if (key === 'fbclid') return String(value) !== '';
    return String(value).trim() !== '';
  }

  function storageGet(store, key) {
    try {
      var value = store.getItem(key);
      return isNonEmpty(key, value) ? normalizeValue(key, value) : '';
    } catch (e) {
      return '';
    }
  }

  function storageSet(store, key, value) {
    if (!isNonEmpty(key, value)) return;
    try {
      store.setItem(key, normalizeValue(key, value));
    } catch (e) {}
  }

  function dualWrite(key, value) {
    if (!isNonEmpty(key, value)) return;
    var stored = normalizeValue(key, value);
    storageSet(window.sessionStorage, key, stored);
    storageSet(window.localStorage, key, stored);
  }

  function readStorage(key) {
    var fromSession = storageGet(window.sessionStorage, key);
    if (fromSession) return fromSession;
    return storageGet(window.localStorage, key);
  }

  function fbclidFromFbcCookie() {
    var fbc = getCookie('_fbc');
    if (!fbc) return '';
    // Format: fb.1.{creationTimeMs}.{fbclid}
    var parts = String(fbc).split('.');
    if (parts.length < 4) return '';
    var fbclid = parts.slice(3).join('.');
    return fbclid ? fbclid : '';
  }

  function truncateReferrer(raw) {
    if (!raw) return '';
    var s = String(raw).trim();
    if (!s) return '';
    if (s.length <= REFERRER_MAX_LEN) return s;
    return s.slice(0, REFERRER_MAX_LEN);
  }

  function detectBrowser() {
    try {
      var ua = (navigator.userAgent || '').toLowerCase();
      if (!ua) return '';
      if (ua.indexOf('edg/') !== -1 || ua.indexOf('edge/') !== -1) return 'edge';
      if (ua.indexOf('opr/') !== -1 || ua.indexOf('opera') !== -1) return 'opera';
      if (ua.indexOf('chrome/') !== -1 && ua.indexOf('chromium') === -1) return 'chrome';
      if (ua.indexOf('firefox/') !== -1 || ua.indexOf('fxios/') !== -1) return 'firefox';
      if (ua.indexOf('safari/') !== -1 && ua.indexOf('chrome/') === -1) return 'safari';
      if (ua.indexOf('samsungbrowser/') !== -1) return 'samsung';
      return 'other';
    } catch (e) {
      return '';
    }
  }

  function detectClientDevice() {
    try {
      var ua = navigator.userAgent || '';
      if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return 'tablet';
      if (/Mobi|iPhone|iPod|Android.*Mobile|webOS|BlackBerry|IEMobile/i.test(ua)) {
        return 'mobile';
      }
      return 'desktop';
    } catch (e) {
      return '';
    }
  }

  function readAnonId() {
    var fromSession = storageGet(window.sessionStorage, 'anon_id');
    if (fromSession) return fromSession;
    var fromLocal = storageGet(window.localStorage, 'nc_anon_id');
    if (fromLocal) return fromLocal;
    var fromCookie = getCookie('_nc_aid');
    return fromCookie ? String(fromCookie).trim() : '';
  }

  /** Session-only; set on index after lp_full is shown. Never infer from assignment. */
  function readHomepageVersion() {
    var value = storageGet(window.sessionStorage, 'homepage_version');
    if (value === 'a' || value === 'b') return value;
    return '';
  }

  /**
   * Session-only; set by the homepage hero (ab_lp-hero-sachet-watch) when the
   * hero_image / hero_headline arm is rendered. Never infer from assignment.
   */
  function readSeenVersion(key) {
    var value = storageGet(window.sessionStorage, key);
    if (value === 'a' || value === 'b') return value;
    return '';
  }

  function pickFromSearch(search) {
    var out = {};
    try {
      var params = new URLSearchParams(
        search != null ? search : window.location.search
      );
      KEYS.forEach(function (key) {
        var value = params.get(key);
        if (!isNonEmpty(key, value)) return;
        out[key] = normalizeValue(key, value);
      });
    } catch (err) {
      console.warn('[web-attribution] pickFromSearch', err);
    }
    return out;
  }

  function pickFromStorage() {
    var out = {};
    KEYS.forEach(function (key) {
      var value = readStorage(key);
      if (value) out[key] = value;
    });
    return out;
  }

  /** sessionStorage first (legacy API), then localStorage. */
  function pickFromSession() {
    return pickFromStorage();
  }

  /**
   * Full attribution map for auth URLs / analytics.
   * Priority: current URL → sessionStorage → localStorage → `_fbc` (fbclid).
   */
  function pickAttribution(search) {
    var out = {};
    var fromUrl = pickFromSearch(search);
    var fromStorage = pickFromStorage();

    KEYS.forEach(function (key) {
      if (fromUrl[key]) {
        out[key] = fromUrl[key];
        return;
      }
      if (fromStorage[key]) {
        out[key] = fromStorage[key];
        return;
      }
      if (key === 'fbclid') {
        var fromFbc = fbclidFromFbcCookie();
        if (fromFbc) out.fbclid = fromFbc;
      }
    });

    var referrer = readStorage('referrer_url');
    if (!referrer) {
      try {
        referrer = truncateReferrer(document.referrer);
      } catch (e) {}
    }
    if (referrer) out.referrer_url = truncateReferrer(referrer);

    var anonId = readAnonId();
    if (anonId) out.anon_id = anonId;

    var browser = detectBrowser();
    if (browser) out.browser = browser;

    var clientDevice = detectClientDevice();
    if (clientDevice) out.client_device = clientDevice;

    var homepageVersion = readHomepageVersion();
    if (homepageVersion) out.homepage_version = homepageVersion;

    var headerImageVersion = readSeenVersion('header_image_version');
    if (headerImageVersion) out.header_image_version = headerImageVersion;

    var headerHeadlineVersion = readSeenVersion('header_headline_version');
    if (headerHeadlineVersion) out.header_headline_version = headerHeadlineVersion;

    return out;
  }

  function captureFromUrl(search) {
    var out = {};
    try {
      var fromUrl = pickFromSearch(search);
      Object.keys(fromUrl).forEach(function (key) {
        dualWrite(key, fromUrl[key]);
        out[key] = fromUrl[key];
      });
    } catch (err) {
      console.warn('[web-attribution] captureFromUrl', err);
    }
    return out;
  }

  function mergeIntoSession(fromSearch) {
    var fromUrl = pickFromSearch(fromSearch);
    Object.keys(fromUrl).forEach(function (key) {
      dualWrite(key, fromUrl[key]);
    });
    return fromUrl;
  }

  /** Fill empty session keys from localStorage (new tab / storage recovery). */
  function hydrateSessionFromLocal() {
    KEYS.concat(['referrer_url']).forEach(function (key) {
      try {
        var sessionVal = storageGet(window.sessionStorage, key);
        if (sessionVal) return;
        var localVal = storageGet(window.localStorage, key);
        if (localVal) storageSet(window.sessionStorage, key, localVal);
      } catch (e) {}
    });
  }

  /** Promote session values (e.g. from analytics-init) into localStorage. */
  function promoteSessionToLocal() {
    KEYS.concat(['referrer_url']).forEach(function (key) {
      try {
        var sessionVal = storageGet(window.sessionStorage, key);
        if (sessionVal) storageSet(window.localStorage, key, sessionVal);
      } catch (e) {}
    });
  }

  /** First-touch referrer: keep the earliest non-empty value across session + local. */
  function captureReferrerFirstTouch() {
    try {
      var existing = readStorage('referrer_url');
      if (existing) {
        dualWrite('referrer_url', truncateReferrer(existing));
        return;
      }
      var raw = truncateReferrer(document.referrer);
      if (raw) dualWrite('referrer_url', raw);
    } catch (e) {}
  }

  /** If fbclid only exists on `_fbc`, persist it for later reads. */
  function recoverFbclidFromCookie() {
    if (readStorage('fbclid')) return;
    var fromFbc = fbclidFromFbcCookie();
    if (fromFbc) dualWrite('fbclid', fromFbc);
  }

  function appendToUrl(urlString) {
    if (!urlString) return urlString;
    var tracking = pickAttribution();
    var keys = KEYS.concat(CONTEXT_KEYS);
    var hasAny = keys.some(function (key) {
      return isNonEmpty(key, tracking[key]);
    });
    if (!hasAny) return urlString;

    try {
      var u = new URL(urlString, window.location.origin);
      keys.forEach(function (key) {
        if (isNonEmpty(key, tracking[key])) {
          u.searchParams.set(key, normalizeValue(key, tracking[key]));
        }
      });
      return u.toString();
    } catch (err) {
      var join = urlString.indexOf('?') === -1 ? '?' : '&';
      var parts = keys
        .filter(function (key) {
          return isNonEmpty(key, tracking[key]);
        })
        .map(function (key) {
          return (
            encodeURIComponent(key) +
            '=' +
            encodeURIComponent(normalizeValue(key, tracking[key]))
          );
        });
      return parts.length ? urlString + join + parts.join('&') : urlString;
    }
  }

  window.NutricodeWebAttribution = {
    KEYS: KEYS,
    CONTEXT_KEYS: CONTEXT_KEYS,
    captureFromUrl: captureFromUrl,
    pickFromSession: pickFromSession,
    pickFromStorage: pickFromStorage,
    pickFromSearch: pickFromSearch,
    pickAttribution: pickAttribution,
    mergeIntoSession: mergeIntoSession,
    appendToUrl: appendToUrl
  };

  hydrateSessionFromLocal();
  captureFromUrl();
  promoteSessionToLocal();
  captureReferrerFirstTouch();
  recoverFbclidFromCookie();
})();
